import { Link } from 'react-router-dom';
import styles from './Documentation.module.css';

const SECTIONS = [
  {
    title: 'Getting Started',
    description: 'Quick start guide for new users. Learn the basics of running experiments, configuring parameters, and interpreting results.',
    topics: [
      { name: 'Introduction to AxLabs', path: '#introduction' },
      { name: 'Account Setup', path: '#account' },
      { name: 'Navigating the Interface', path: '#navigation' },
      { name: 'Running Your First Experiment', path: '#first-experiment' },
      { name: 'Exporting Data', path: '#export' },
    ],
  },
  {
    title: 'For Students',
    description: 'Guides for effective learning with virtual laboratories. Understanding simulations, interpreting results, and avoiding common pitfalls.',
    topics: [
      { name: 'How Simulations Work', path: '#how-simulations-work' },
      { name: 'Making Predictions', path: '#predictions' },
      { name: 'Analyzing Results', path: '#analysis' },
      { name: 'Understanding Error Messages', path: '#errors' },
      { name: 'Study Strategies', path: '#study-strategies' },
    ],
  },
  {
    title: 'For Teachers',
    description: 'Resources for incorporating AxLabs into courses. Assignment design, assessment strategies, and curriculum alignment.',
    topics: [
      { name: 'Course Integration', path: '#integration' },
      { name: 'Designing Assignments', path: '#assignments' },
      { name: 'Assessment Strategies', path: '#assessment' },
      { name: 'Curriculum Alignment', path: '#alignment' },
      { name: 'Best Practices', path: '#best-practices' },
    ],
  },
  {
    title: 'For Developers',
    description: 'Technical documentation for extending AxLabs or building integrations. API reference, contribution guidelines, and architecture overview.',
    topics: [
      { name: 'Architecture Overview', path: '#architecture' },
      { name: 'Experiment Development', path: '#experiment-dev' },
      { name: 'API Reference', path: '#api' },
      { name: 'Contribution Guidelines', path: '#contributing' },
      { name: 'Self-Hosting', path: '#self-hosting' },
    ],
  },
  {
    title: 'Reference',
    description: 'Detailed reference material. Physical constants, mathematical formulas, and numerical methods documentation.',
    topics: [
      { name: 'Physical Constants', path: '#constants' },
      { name: 'Mathematical Reference', path: '#math' },
      { name: 'Numerical Methods', path: '#numerical' },
      { name: 'Units and Conversions', path: '#units' },
      { name: 'Glossary', path: '#glossary' },
    ],
  },
];

const QUICK_START = [
  {
    step: '1',
    title: 'Choose an Experiment',
    description: 'Browse available experiments by subject or use search to find specific topics. Each experiment includes theoretical background and learning objectives.',
  },
  {
    step: '2',
    title: 'Set Parameters',
    description: 'Configure experiment parameters within valid ranges. The interface indicates constraints and suggests typical values. Parameters determine initial conditions.',
  },
  {
    step: '3',
    title: 'Run Simulation',
    description: 'Execute the simulation and observe results in real-time. Monitor numerical indicators for convergence and stability. Pause and restart as needed.',
  },
  {
    step: '4',
    title: 'Analyze Data',
    description: 'Use built-in analysis tools or export data for external processing. Compare results to theoretical predictions. Identify discrepancies and their causes.',
  },
  {
    step: '5',
    title: 'Iterate',
    description: 'Modify parameters based on insights and repeat. Systematic exploration builds intuition and understanding. Document observations and conclusions.',
  },
];

export function Documentation() {
  return (
    <div className={styles.documentation}>
      {/* Header */}
      <header className={styles.header}>
        <div className={styles.headerContainer}>
          <Link to="/" className={styles.backLink}>← Back to Home</Link>
          <h1 className={styles.title}>Documentation</h1>
          <p className={styles.subtitle}>
            Comprehensive guides for students, teachers, and developers.
            Learn to use AxLabs effectively for science education and research.
          </p>
        </div>
      </header>

      {/* Quick Start */}
      <section className={styles.section}>
        <div className={styles.sectionContainer}>
          <h2 className={styles.sectionTitle}>Quick Start</h2>
          <div className={styles.quickStartGrid}>
            {QUICK_START.map((item) => (
              <div key={item.step} className={styles.stepCard}>
                <span className={styles.stepNumber}>{item.step}</span>
                <h3 className={styles.stepTitle}>{item.title}</h3>
                <p className={styles.stepDesc}>{item.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Documentation Sections */}
      <section className={styles.sectionAlt}>
        <div className={styles.sectionContainer}>
          <h2 className={styles.sectionTitle}>Documentation Sections</h2>
          <div className={styles.docsGrid}>
            {SECTIONS.map((section) => (
              <div key={section.title} className={styles.docCard}>
                <h3 className={styles.docTitle}>{section.title}</h3>
                <p className={styles.docDesc}>{section.description}</p>
                <ul className={styles.docTopics}>
                  {section.topics.map((topic) => (
                    <li key={topic.name} className={styles.docTopic}>
                      <a href={topic.path} className={styles.docLink}>{topic.name}</a>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Getting Started Detail */}
      <section className={styles.section}>
        <div className={styles.sectionContainer}>
          <h2 className={styles.sectionTitle}>Getting Started with AxLabs</h2>
          <div className={styles.contentBlock}>
            <h3 className={styles.contentTitle}>What is AxLabs?</h3>
            <p className={styles.contentText}>
              AxLabs provides research-grade virtual laboratories for physics, chemistry, biology, and mathematics.
              Unlike educational games that simplify for engagement, AxLabs solves real governing equations with
              documented numerical methods.
            </p>
            <p className={styles.contentText}>
              Each experiment produces deterministic, reproducible results. You can export data for analysis
              in external tools. Results are suitable for legitimate scientific inquiry and fair assessment.
            </p>

            <h3 className={styles.contentTitle}>Account Setup</h3>
            <p className={styles.contentText}>
              Create an account with your institutional email or personal credentials. Verified accounts can
              save experiment configurations and access teacher features. Guest access is available for
              limited functionality.
            </p>

            <h3 className={styles.contentTitle}>Running Experiments</h3>
            <p className={styles.contentText}>
              Select an experiment from the library. Configure parameters within the indicated ranges.
              Click Run to execute the simulation. Results appear in real-time with graphs, tables, and
              numerical indicators.
            </p>

            <h3 className={styles.contentTitle}>Exporting Data</h3>
            <p className={styles.contentText}>
              Use the Export button to save data in CSV, JSON, or HDF5 formats. Exported data includes
              complete metadata: parameters, timestamps, and version information. This ensures
              reproducibility and enables external analysis.
            </p>
          </div>
        </div>
      </section>

      {/* For Students */}
      <section className={styles.sectionAlt}>
        <div className={styles.sectionContainer}>
          <h2 className={styles.sectionTitle}>For Students</h2>
          <div className={styles.contentBlock}>
            <h3 className={styles.contentTitle}>Learning with Simulations</h3>
            <p className={styles.contentText}>
              Simulations are most effective when you make predictions before running experiments.
              Write down what you expect to happen. Then run the simulation and compare. Discrepancies
              between prediction and result reveal misconceptions.
            </p>

            <h3 className={styles.contentTitle}>Understanding Failure</h3>
            <p className={styles.contentText}>
              Experiments can fail in AxLabs. Parameters may be outside valid ranges. Numerical methods
              may not converge. These failures are informative—they indicate limits of models or
              inappropriate experimental conditions. Diagnose failures rather than avoiding them.
            </p>

            <h3 className={styles.contentTitle}>Analysis Tips</h3>
            <p className={styles.contentText}>
              Start with qualitative observations: What trends do you see? What variables affect the outcome?
              Then proceed to quantitative analysis: Fit curves, calculate derived quantities, compare
              to theory. Use the analysis tools or export to your preferred environment.
            </p>

            <h3 className={styles.contentTitle}>Common Pitfalls</h3>
            <ul className={styles.contentList}>
              <li>Changing multiple parameters at once—isolate effects by varying one parameter at a time</li>
              <li>Ignoring numerical warnings—they indicate when approximations may be unreliable</li>
              <li>Over-fitting data—simpler models often capture the essential physics</li>
              <li>Extrapolating beyond valid ranges—models have limits outside their domain of applicability</li>
            </ul>
          </div>
        </div>
      </section>

      {/* For Teachers */}
      <section className={styles.section}>
        <div className={styles.sectionContainer}>
          <h2 className={styles.sectionTitle}>For Teachers</h2>
          <div className={styles.contentBlock}>
            <h3 className={styles.contentTitle}>Course Integration</h3>
            <p className={styles.contentText}>
              AxLabs experiments align with standard curricula in physics, chemistry, biology, and mathematics.
              Use the Curriculum page to find experiments matching your learning objectives. Each experiment
              tags relevant concepts and prerequisite knowledge.
            </p>

            <h3 className={styles.contentTitle}>Assignment Design</h3>
            <p className={styles.contentText}>
              Effective assignments combine prediction, experiment, and analysis. Ask students to predict
              outcomes before running simulations. Require comparison to theory. Use unique parameter sets
              for each student to enable collaboration while preventing copying.
            </p>

            <h3 className={styles.contentTitle}>Assessment</h3>
            <p className={styles.contentText}>
              Deterministic outputs enable objective assessment. Each student receives unique parameters
              with equivalent difficulty. Exported data allows verification of results. Use the Assignment
              Builder to create and distribute assessment tasks.
            </p>

            <h3 className={styles.contentTitle}>Best Practices</h3>
            <ul className={styles.contentList}>
              <li>Introduce simulations after theoretical background—simulations reinforce, not replace, theory</li>
              <li>Require written predictions before running experiments</li>
              <li>Ask students to explain discrepancies between prediction and result</li>
              <li>Use systematic parameter variation to build quantitative intuition</li>
              <li>Combine virtual and physical laboratories when possible</li>
            </ul>
          </div>
        </div>
      </section>

      {/* For Developers */}
      <section className={styles.sectionAlt}>
        <div className={styles.sectionContainer}>
          <h2 className={styles.sectionTitle}>For Developers</h2>
          <div className={styles.contentBlock}>
            <h3 className={styles.contentTitle}>Architecture</h3>
            <p className={styles.contentText}>
              AxLabs is built with React, TypeScript, and a custom simulation engine. The simulation core
              solves differential equations using symplectic integrators and adaptive methods. All
              experiments share a common architecture for consistency and maintainability.
            </p>

            <h3 className={styles.contentTitle}>API Reference</h3>
            <p className={styles.contentText}>
              The AxLabs API provides programmatic access to experiments. Configure parameters, run
              simulations, and retrieve results via REST endpoints. API authentication uses tokens
              associated with user accounts.
            </p>

            <h3 className={styles.contentTitle}>Contributing</h3>
            <p className={styles.contentText}>
              Contributions are welcome. New experiments must follow validation guidelines and include
              theoretical background, numerical methods documentation, and comparison to analytical
              solutions or experimental data. Submit pull requests on GitHub.
            </p>

            <h3 className={styles.contentTitle}>Self-Hosting</h3>
            <p className={styles.contentText}>
              Institutions can deploy their own AxLabs instances. Docker containers simplify deployment.
              The codebase is open source—inspect, modify, and extend to meet specific needs. Contact
              us for enterprise support options.
            </p>
          </div>
        </div>
      </section>

      {/* Additional Resources */}
      <section className={styles.section}>
        <div className={styles.sectionContainer}>
          <h2 className={styles.sectionTitle}>Additional Resources</h2>
          <div className={styles.resourcesList}>
            <a href="https://github.com/staticpayload/axlabs" className={styles.resourceCard} target="_blank" rel="noopener noreferrer">
              <h3 className={styles.resourceTitle}>GitHub Repository</h3>
              <p className={styles.resourceDesc}>Source code, issue tracking, and contribution guidelines</p>
            </a>
            <Link to="/research" className={styles.resourceCard}>
              <h3 className={styles.resourceTitle}>Research & Validation</h3>
              <p className={styles.resourceDesc}>Numerical methods documentation and validation studies</p>
            </Link>
            <Link to="/about" className={styles.resourceCard}>
              <h3 className={styles.resourceTitle}>About AxLabs</h3>
              <p className={styles.resourceDesc}>Project history, team, and technical philosophy</p>
            </Link>
            <Link to="/contact" className={styles.resourceCard}>
              <h3 className={styles.resourceTitle}>Contact</h3>
              <p className={styles.resourceDesc}>Get help with technical issues or feature requests</p>
            </Link>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className={styles.footer}>
        <div className={styles.footerContainer}>
          <a href="https://github.com/staticpayload/axlabs" className={styles.footerLink} target="_blank" rel="noopener noreferrer">GitHub</a>
          <span className={styles.footerSeparator}>·</span>
          <Link to="/contact" className={styles.footerLink}>Contact</Link>
          <span className={styles.footerSeparator}>·</span>
          <Link to="/experiments" className={styles.footerLink}>Browse Experiments</Link>
        </div>
      </footer>
    </div>
  );
}
